import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import * as React from "react";
import { useTranslation } from "react-i18next";
import NavSearch from "@/components/common/navContainers/NavSearch";
import { TooltipProvider } from "@/components/ui/tooltip";
import AblyProvider from "@/contexts/common/AblyProvider";
import useCollapsibleNavSections from "@/hooks/useCollapsibleNavSections";
import { useSearchableNavItems } from "@/hooks/useSearchableNavItems";
import useScreenSize from "@/hooks/useScreenSize";
import { SIDENAV_FADE_DURATION } from "@/types/common/library";
import Icon, { ICON_TYPE } from "./Icon";
import Button from "./Button";
import { NayLayoutProps } from "./NavLayout";
import { GlobalNavigationItem } from "./NavLayoutItem";
import NavLayoutSidebarFooter from "./NavLayoutSidebarFooter";

type SidebarProps = {
  isMobileMenuOpen?: boolean;
  sideBarRef?: React.RefObject<HTMLElement | null>;
  isMobile?: boolean;
  isSuperAdmin?: boolean;
  className?: string;
  logo: React.ReactNode;
  minimizedLogo: React.ReactNode;
  sideNavMessage?: string;
  userHasAlert: boolean;
  onItemClick?: () => void;
  onMinimizeChange?: (isMinimized: boolean) => void;
  isDimmed?: boolean;
} & Pick<NayLayoutProps, "user" | "userActions" | "menuSections">;

const NavLayoutSidebar: React.FC<SidebarProps> = ({
  className,
  sideBarRef,
  menuSections = [],
  user,
  isMobileMenuOpen,
  isMobile,
  isSuperAdmin,
  userActions,
  logo,
  minimizedLogo,
  sideNavMessage,
  userHasAlert,
  onItemClick,
  onMinimizeChange,
  isDimmed = false,
}) => {
  const { t: ct } = useTranslation("common");
  const { isMobile: screenIsMobile } = useScreenSize();
  const hasFooter = !!user?.id && !!user?.name;

  // Mobile navigation state
  const [selectedSection, setSelectedSection] = React.useState<string | null>(null);

  const scopeKey = React.useMemo(() => {
    // Coarse scope by hostname; orgs share state today. Swap to user/org key when needed.
    if (globalThis.window === undefined) return "global";
    return globalThis.location.host || "global";
  }, []);

  const { hasHeader, isCollapsed, toggleSection } = useCollapsibleNavSections(
    menuSections,
    scopeKey
  );

  // Minimize state with localStorage persistence
  const [isMinimized, setIsMinimized] = React.useState(false);

  // Auto-collapse bookkeeping: when the assistant dock takes the stage the
  // rail minimizes on its own. These refs remember what to restore on close
  // and keep the automatic flips out of the user's persisted preference.
  const preDockMinimizedRef = React.useRef<boolean | null>(null);
  const skipPersistRef = React.useRef(false);
  const prevDimmedRef = React.useRef(isDimmed);

  const sidebarWidth = isMobile ? "100%" : isMinimized ? "64px" : "14rem";

  // Initialize from localStorage on mount (desktop only)
  React.useEffect(() => {
    if (!isMobile && globalThis.window !== undefined) {
      const stored = localStorage.getItem("nav-sidebar-minimized");
      if (stored === "true") {
        setIsMinimized(true);
      }
    }
  }, [isMobile]);

  // Sync state changes to localStorage and notify parent. Automatic flips
  // (dock open/close) notify the layout but don't overwrite the user's
  // stored preference, so a reload with the dock closed comes back right.
  React.useEffect(() => {
    if (!isMobile && globalThis.window !== undefined) {
      if (skipPersistRef.current) {
        skipPersistRef.current = false;
      } else {
        localStorage.setItem("nav-sidebar-minimized", isMinimized.toString());
      }
      onMinimizeChange?.(isMinimized);
    }
  }, [isMinimized, isMobile, onMinimizeChange]);

  React.useEffect(() => {
    if (isMobile) return;
    if (prevDimmedRef.current === isDimmed) return;
    prevDimmedRef.current = isDimmed;

    if (isDimmed) {
      preDockMinimizedRef.current = isMinimized;
      if (!isMinimized) {
        skipPersistRef.current = true;
        setIsMinimized(true);
      }
    } else if (preDockMinimizedRef.current !== null) {
      const restoreTo = preDockMinimizedRef.current;
      preDockMinimizedRef.current = null;
      if (restoreTo !== isMinimized) {
        skipPersistRef.current = true;
        setIsMinimized(restoreTo);
      }
    }
  }, [isDimmed, isMinimized, isMobile]);

  // Reset to expanded on mobile
  React.useEffect(() => {
    if (isMobile) {
      setIsMinimized(false);
    }
  }, [isMobile]);

  // Reset mobile navigation state when switching between mobile/desktop
  React.useEffect(() => {
    if (!isMobile) {
      setSelectedSection(null);
    }
  }, [isMobile]);

  const handleMinimize = () => {
    if (preDockMinimizedRef.current !== null) {
      preDockMinimizedRef.current = !isMinimized;
    }
    setIsMinimized(!isMinimized);
  };

  const handleLogoClick = () => {
    if (isMinimized) {
      if (preDockMinimizedRef.current !== null) {
        preDockMinimizedRef.current = false;
      }
      setIsMinimized(false);
    }
  };

  // Mobile navigation handlers
  const handleSectionClick = (sectionName: string) => {
    if (isMobile) {
      setSelectedSection(sectionName);
    }
  };

  const handleBackToHeaders = () => {
    setSelectedSection(null);
  };

  const searchableItems = useSearchableNavItems();

  return (
    <TooltipProvider delayDuration={100}>
      <motion.aside
        data-open={!!isMobileMenuOpen}
        id="nayLayout_sidebar"
        ref={sideBarRef}
        style={
          {
            "--fade-duration": `${SIDENAV_FADE_DURATION}ms`,
            transition: "width var(--fade-duration)",
            width: sidebarWidth,
          } as React.CSSProperties
        }
        className={clsx(
          "bg-surface",
          "border-r",
          "dark:border-r-neutral",
          "px-4",
          "pb-4",
          "self-stretch",
          "flex",
          "flex-col",
          "gap-4",
          "items-stretch",
          "justify-start",
          "fixed",
          "top-16",
          "left-0",
          "right-0",
          "bottom-0",
          "z-10",
          isMobile && "w-full",
          "md:sticky",
          "md:top-0",
          "md:translate-x-0",
          // Cap the aside at viewport height so the inner `flex-1
          // min-h-0 overflow-auto` nav list (below) scrolls within
          // the sidebar instead of pushing the sidebar past the
          // viewport and hijacking the page scroll.
          "md:h-screen",
          className,
          {
            ["translate-x-0 !z-50"]: isMobile && isMobileMenuOpen,
            ["translate-x-full"]: isMobile && !isMobileMenuOpen,
          }
        )}
        animate={
          isMobile
            ? {
                opacity: isMobileMenuOpen ? 1 : 0,
                x: isMobileMenuOpen ? 0 : "100%",
              }
            : {}
        }
        transition={{
          duration: SIDENAV_FADE_DURATION / 1000,
          ease: "easeInOut",
        }}
        data-minimized={isMinimized}
      >
        {!screenIsMobile && (
          <div
            className="grid"
            style={{
              gridTemplateColumns: isMinimized ? "1fr" : "1fr 40px",
              transition: "grid-template-columns var(--fade-duration)",
            }}
          >
            <div
              className="hidden md:block relative mt-4 text-lulaRed text-center"
              style={{ height: "32px", overflow: "hidden" }} // Reduced height for slimmer logo area
            >
              {/* Full logo - visible when expanded; logo prop supplies its own interactive element */}
              <div
                aria-hidden={isMinimized ? true : undefined}
                style={{
                  transition: "opacity var(--fade-duration)",
                  opacity: isMinimized ? 0 : 1,
                  position: "absolute",
                  left: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "65px",
                  height: "32px",
                  overflow: "hidden",
                  pointerEvents: isMinimized ? "none" : "auto",
                }}
              >
                {logo}
              </div>
              <Button
                type="none"
                aria-label={ct("a11y.expandSidebar")}
                aria-hidden={isMinimized ? undefined : true}
                tabIndex={isMinimized ? undefined : -1}
                onClick={handleLogoClick}
                className="group !bg-transparent !p-0 !m-0 !border-none !rounded-none"
                style={{
                  transition: "opacity var(--fade-duration)",
                  opacity: isMinimized ? 1 : 0,
                  position: "absolute",
                  left: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "23px",
                  height: "23px",
                  pointerEvents: isMinimized ? "auto" : "none",
                }}
              >
                <span className="absolute inset-0 flex items-center justify-start overflow-hidden transition-opacity duration-150 group-hover:opacity-0">
                  {minimizedLogo}
                </span>
                <span
                  aria-hidden="true"
                  className="absolute inset-0 flex items-center justify-center text-700 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                  style={{ transform: "scaleX(-1)" }}
                >
                  <Icon icon="arrow-left-to-line" type={ICON_TYPE.SOLID} />
                </span>
              </Button>
            </div>
            {!isMinimized && (
              <Button
                type="none"
                compact
                iconOnly
                onClick={handleMinimize}
                aria-label={ct("a11y.collapseSidebar")}
                className="!mt-3 hover:bg-200"
              >
                <Icon icon="arrow-left-to-line" type={ICON_TYPE.SOLID} />
              </Button>
            )}
          </div>
        )}
        {sideNavMessage && (
          <div className={clsx("mt-8", "p-4", "rounded-xl", "bg-panel", "shadow")}>
            {sideNavMessage}
          </div>
        )}
        {/* Navigation Search */}
        {!isSuperAdmin && searchableItems.length > 0 && (
          <div className={clsx(screenIsMobile && "px-2")}>
            <NavSearch items={searchableItems} isMinimized={isMinimized} />
          </div>
        )}
        <div className={clsx("relative", "flex-1", "mx-[-1rem]", "min-h-0")}>
          {/* Top fade overlay */}
          <div
            className={clsx(
              "top-0",
              "right-0",
              "left-0",
              "z-10",
              "absolute",
              "bg-gradient-to-b",
              "from-surface",
              "to-transparent",
              "h-4",
              "pointer-events-none"
            )}
          />

          {/* Bottom fade overlay */}
          <div
            className={clsx(
              "right-0",
              "bottom-0",
              "left-0",
              "z-10",
              "absolute",
              "bg-gradient-to-t",
              "from-surface",
              "to-transparent",
              "h-4",
              "pointer-events-none"
            )}
          />

          <div
            className={clsx("h-full", "overflow-auto", "gap-6", "flex", "flex-col", "pt-2", {
              "border-b-1 border-neutral": !menuSections.some(
                (section) => section.position === "bottom"
              ),
            })}
          >
            {isMobile ? (
              // Mobile Navigation Views
              <div className="relative h-full">
                {/* Mobile Section Headers View */}
                <AnimatePresence mode="wait">
                  {!selectedSection && (
                    <motion.div
                      key="mobile-headers"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: SIDENAV_FADE_DURATION / 1000 }}
                      className="flex flex-col gap-2 h-full"
                    >
                      {!!menuSections.length &&
                        menuSections
                          .filter((section) => section.position !== "bottom")
                          .map((section, index, array) => {
                            const header = hasHeader(section);
                            if (!header) return null;
                            const isLast = index === array.length - 1;

                            return (
                              <div key={`mobile-header-${section.name}`}>
                                <button
                                  type="button"
                                  onClick={() => handleSectionClick(section.name as string)}
                                  className={clsx(
                                    "w-full",
                                    "py-3",
                                    "px-4",
                                    "text-left",
                                    "hover:bg-200",
                                    "transition-colors",
                                    "flex",
                                    "justify-between",
                                    "items-center"
                                  )}
                                >
                                  <span className="font-medium text-foreground text-sm">
                                    {section.name}
                                  </span>
                                  <Icon
                                    icon="chevron-right"
                                    type={ICON_TYPE.SOLID}
                                    className="text-600"
                                  />
                                </button>
                                {!isLast && <hr className="mx-4 mt-2 border-neutral" />}
                              </div>
                            );
                          })}
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Mobile Section Detail View */}
                <AnimatePresence mode="wait">
                  {selectedSection && (
                    <motion.div
                      key="mobile-detail"
                      initial={{ x: "100%" }}
                      animate={{ x: 0 }}
                      exit={{ x: "100%" }}
                      transition={{
                        duration: SIDENAV_FADE_DURATION / 1000,
                        ease: "easeInOut",
                      }}
                      className="absolute inset-0 flex flex-col bg-surface"
                    >
                      {/* Back Button Header */}
                      <div className="flex items-center gap-3 p-4 border-neutral border-b">
                        <button
                          type="button"
                          onClick={handleBackToHeaders}
                          className={clsx(
                            "p-2",
                            "rounded-lg",
                            "hover:bg-200",
                            "transition-colors",
                            "flex",
                            "items-center",
                            "justify-center"
                          )}
                        >
                          <Icon icon="arrow-left" type={ICON_TYPE.SOLID} />
                        </button>
                        <button
                          type="button"
                          onClick={handleBackToHeaders}
                          className={clsx(
                            "font-medium",
                            "text-foreground",
                            "text-lg",
                            "hover:text-primary",
                            "transition-colors",
                            "text-left"
                          )}
                        >
                          {selectedSection}
                        </button>
                      </div>

                      {/* Section Items */}
                      <div className="flex-1 p-4 overflow-auto">
                        {menuSections
                          .filter((section) => section.name === selectedSection)
                          .map((section) => (
                            <div key={`detail-${section.name}`} className="space-y-2">
                              {section.items.map((item) => (
                                <GlobalNavigationItem
                                  key={`${item.label}_${item.icon}`}
                                  {...item}
                                  onItemClick={onItemClick}
                                  isMinimized={false}
                                />
                              ))}
                            </div>
                          ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              // Desktop Navigation (existing logic)
              !!menuSections.length &&
              menuSections
                .filter((section) => section.position !== "bottom")
                .map((section) => {
                  const header = hasHeader(section);
                  const collapsed = isCollapsed(section);

                  return (
                    <div
                      key={`section_${section.name || "unnamed"}`}
                      className={clsx(
                        "transition-opacity duration-300",
                        isDimmed &&
                          !section.items.some((item) => item.variant === "assistant") &&
                          "opacity-50 hover:opacity-100"
                      )}
                    >
                      {header && (
                        <div className="relative mb-2" style={{ height: "20px" }}>
                          {/* Section title - visible when expanded */}
                          <button
                            type="button"
                            aria-expanded={!collapsed}
                            id={
                              section.sectionKey ? `tour-section-${section.sectionKey}` : undefined
                            }
                            onClick={() => toggleSection(section.name as string)}
                            style={{
                              transition: "opacity var(--fade-duration)",
                              opacity: isMinimized ? 0 : 1,
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "20px",
                            }}
                            className={clsx(
                              "text-600",
                              "text-sm",
                              "w-full",
                              "flex",
                              "items-center",
                              "gap-1",
                              "select-none",
                              "text-sm",
                              "px-6",
                              "hover:!text-primary",
                              "focus:text-600"
                            )}
                          >
                            <span
                              className={clsx("block")}
                              style={{
                                transition: "opacity var(--fade-duration)",
                                opacity: isMinimized ? 0 : 1,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {section.name}
                            </span>
                            <motion.span
                              className={clsx(
                                "inline-flex",
                                "items-center",
                                "justify-center",
                                "origin-center"
                              )}
                              animate={{ rotate: collapsed ? 0 : 180 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              style={{
                                transition: "opacity var(--fade-duration)",
                                opacity: isMinimized ? 0 : 1,
                              }}
                            >
                              <Icon icon="caret-down" type={ICON_TYPE.SOLID} />
                            </motion.span>
                          </button>
                          {/* HR - visible when minimized */}
                          <hr
                            className="bg-300"
                            style={{
                              transition: "opacity var(--fade-duration)",
                              opacity: isMinimized ? 1 : 0,
                              position: "absolute",
                              top: "0",
                              left: "16px",
                              right: "16px",
                              transform: "translateY(-50%)",
                              height: "1px",
                              border: "none",
                            }}
                          />
                        </div>
                      )}
                      {header ? (
                        <AnimatePresence initial={false}>
                          {!collapsed && (
                            <motion.div
                              key={`section-items-${section.name}`}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              style={{ overflow: "hidden" }}
                            >
                              <div className={clsx("grid", "self-stretch")}>
                                {section.items.map((item) => (
                                  <GlobalNavigationItem
                                    key={`${item.label}_${item.icon}`}
                                    {...item}
                                    onItemClick={onItemClick}
                                    isMinimized={isMinimized}
                                  />
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      ) : (
                        <>
                          {!isSuperAdmin && (
                            <div className={clsx("pl-4", "mt-6", "mb-2")} aria-hidden="true" />
                          )}
                          <div
                            className={clsx("grid", "self-stretch", "gap-1", {
                              "px-2": (section.name?.length || 0) > 0,
                            })}
                          >
                            {section.items.map((item) => (
                              <GlobalNavigationItem
                                key={`${item.label}_${item.icon}`}
                                {...item}
                                onItemClick={onItemClick}
                                isMinimized={isMinimized}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
            )}
          </div>
        </div>
        {hasFooter && (
          <AblyProvider>
            <div
              className={clsx(
                // mt-auto replicates the footer's own bottom-pinning, which
                // the wrapper would otherwise swallow.
                "mt-auto transition-opacity duration-300",
                isDimmed && "opacity-50 hover:opacity-100"
              )}
            >
              <NavLayoutSidebarFooter
                user={user}
                userActions={userActions}
                hasAlert={userHasAlert}
                bottomSections={menuSections.filter((section) => section.position === "bottom")}
                onItemClick={onItemClick}
                isMinimized={isMinimized}
              />
            </div>
          </AblyProvider>
        )}
      </motion.aside>
    </TooltipProvider>
  );
};

export default NavLayoutSidebar;
